import { useStore } from '@nanostores/react';
import { ClientOnly } from 'remix-utils/client-only';
import { chatStore } from '~/lib/stores/chat';
import { classNames } from '~/utils/classNames';
import { ChatDescription } from '~/lib/persistence/ChatDescription.client';
import logo from '~/assets/dualite-logo.png';
import { Menu } from '~/components/sidebar/Menu.client';
import { useState, useEffect, useRef } from 'react';
import { chatId, description } from '~/lib/persistence/useChatHistory';
import { clearProjectRules } from '~/lib/stores/rules';

// trying out
import { workbenchStore, type WorkbenchViewType } from '~/lib/stores/workbench';
import { Slider, type SliderOptions } from '~/components/ui/Slider';
import { computed } from 'nanostores';
import { useNavigate, useLocation } from '@remix-run/react';
import { selectionStore } from '~/lib/persistence/selectionStore';
import { Headset } from 'lucide-react';
import { ProUpgradeModal } from '~/components/ui/ProUpgradeModal';
import { userStore } from '~/lib/stores/user';
import { isDev } from '~/utils/constants';

const sliderOptions: SliderOptions<WorkbenchViewType> = {
  left: {
    value: 'code',
    text: 'Code',
  },
  right: {
    value: 'preview',
    text: 'Preview',
  },
};

export function Header() {
  const chat = useStore(chatStore);
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const userData = useStore(userStore);

  const selectedView = useStore(workbenchStore.currentView);
  const hasPreview = useStore(computed(workbenchStore.previews, (previews) => previews.length > 0));
  const setSelectedView = (view: WorkbenchViewType) => {
    workbenchStore.currentView.set(view);
  };

  const [isProUpgradeModalOpen, setIsProUpgradeModalOpen] = useState(false);

  const aiModel = useStore(selectionStore).aiModel;
  let model;

  if (aiModel === 'dev-ai-model') {
    model = 'Gemini Flash';
  } else if (aiModel === 'dev-mock-ai-model') {
    model = 'Mock AI Call';
  } else {
    model = 'Claude Sonnet 3.7';
  }

  useEffect(() => {
    if (hasPreview) {
      setSelectedView('preview');
    }
  }, [hasPreview]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Reset chat state when clicking the logo to start a new chat
  const handleLogoClick = (e: React.MouseEvent) => {
    // Reset chatId to undefined for new chat
    const oldChatId = chatId.get();
    console.log('[header] Resetting chat state. Old chatId:', oldChatId);

    chatId.set(undefined);
    description.set(undefined);

    // Verify the chatId was reset
    console.log('[header] ChatId after reset:', chatId.get());

    // Clear project rules for new chat using the dedicated function
    clearProjectRules();
    console.log('[header] Project rules reset for new chat');

    // Let the default link behavior continue
  };

  // TODO: Return null if route is onboarding
  const { pathname } = useLocation();
  const navigate = useNavigate();

  if (pathname.includes('/onboarding')) {
    return null;
  }

  return (
    <header
      style={{
        background: 'rgba(0, 0, 0, 0)',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 5,
      }}
      className={classNames(
        'flex items-center space-between bg-alpha-elements-background-depth-1 pl-1 pt-3 h-[var(--header-height)]',
        {
          'border-transparent w-full gap-5 pr-2': !chat.started,
          'border-alpha-elements-borderColor w-full': chat.started,
        },
      )}
    >
      <div className="flex flex-1 items-center gap-2">
        <div
          ref={menuRef}
          className="ml-2 flex items-center gap-2 z-logo text-alpha-elements-textPrimary cursor-pointer transition-all"
        >
          <ClientOnly>{() => <Menu open={open} />}</ClientOnly>

          <a href="/" className="text-xl font-semibold text-accent flex items-center" onClick={handleLogoClick}>
            {/* <span className="i-alpha:logo-text?mask w-[46px] inline-block" /> */}
            <img src={logo} alt="logo" className="w-[60px] inline-block" />
          </a>
          {chat.started ? (
            <span className="flex-1 px-2 truncate text-center text-alpha-elements-textPrimary">
              <ClientOnly>{() => <ChatDescription />}</ClientOnly>
            </span>
          ) : null}
          <div
            className="i-ph:sidebar-simple-duotone text-xl"
            onClick={() => {
              setOpen(!open);
            }}
          />
        </div>

        {chat.started ? (
          <div className="absolute left-1/2 -translate-x-1/2">
            <Slider selected={selectedView} options={sliderOptions} setSelected={setSelectedView} />
          </div>
        ) : null}

        {isDev && <div className="ml-6 w-fit text-sm text-alpha-elements-textSecondary">Model: {model}</div>}
      </div>

      <div className="flex gap-2">
        <div
          className="cursor-pointer flex items-center gap-2 bg-zinc-800/90 hover:bg-zinc-700 text-gray-200 px-4 py-2 rounded-full text-sm transition-colors duration-150 border border-zinc-700/50 shadow-md whitespace-nowrap"
          onClick={() => {
            window.open('https://calendly.com/dualite-discussion/30min?month=2025-05', '_blank');
          }}
        >
          <Headset className="w-4 h-4" />
          Chat with Alpha Expert
        </div>

        {/* Only show subscription status once we have user data */}
        {userData.email && (
          <>
            {userData.isSubscribed === false && window.location.pathname !== '/billing' ? (
              <div
                className="cursor-pointer hover:bg-zinc-700 text-gray-200 px-4 py-2 rounded-full text-sm transition-colors duration-150 border-2 border-zinc-700/50 shadow-md whitespace-nowrap font-semibold"
                onClick={() => {
                  setIsProUpgradeModalOpen(true);
                }}
              >
                Upgrade to Pro
              </div>
            ) : (
              <div className="cursor-pointer hover:bg-zinc-700 text-gray-200 px-4 py-2 rounded-full text-sm transition-colors duration-150 border-2 border-zinc-700/50 shadow-md whitespace-nowrap font-semibold">
                Pro Account
              </div>
            )}
          </>
        )}

        {/* setting icon */}

        <button
          onClick={() => {
            navigate('/settings');
          }}
          className="flex items-center gap-2 cursor-pointer hover:bg-zinc-700 text-gray-200 px-4 py-2 rounded-full text-sm transition-colors duration-150 border-2 border-zinc-700/50 shadow-md whitespace-nowrap font-semibold"
        >
          <i className="i-ph:gear-duotone text-lg" />
        </button>
      </div>

      <ProUpgradeModal
        isOpen={isProUpgradeModalOpen}
        onClose={() => setIsProUpgradeModalOpen(false)}
        description="Unlock unlimited messages per month and premium support"
      />

      {/* <div className="ml-6 w-fit text-sm text-alpha-elements-textSecondary">
        <IssueDropdown framework="react" />
      </div> */}
      {/* {chat.started && (
        <ClientOnly>
          {() => (
            <div className="mr-1">
              <HeaderActionButtons />
            </div>
          )}
        </ClientOnly>
      )} */}
      {/* {chat.started && <RightTopBar />} */}

      {/* <button
        onClick={() => setIsDeployModalOpen(true)}
        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
      >
      </button> */}

      {/* <DeployModal
        isOpen={isDeployModalOpen}
        onClose={() => setIsDeployModalOpen(false)}
        accessToken="YOUR_OAUTH_TOKEN" // TODO: Get this from OAuth flow
      /> */}
    </header>
  );
}
